import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ShieldCheck } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name?.split(" ")[0] || ""}`);
      // Unverified non-admins must finish email verification before normal flows.
      if (u.role !== "admin" && u.email_verified === false) {
        nav("/pending-verification", { replace: true });
        return;
      }
      const redirect = params.get("redirect");
      if (redirect) nav(redirect);
      else if (u.role === "admin") nav("/dashboard/admin");
      else if (u.role === "seller") nav("/dashboard/seller");
      else nav("/dashboard/buyer");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 lg:grid-rows-1 items-stretch">
      <div className="hidden lg:flex bg-mv-secondary text-white px-12 py-16 flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-mv-primary/40 blur-3xl" />
        <Link to="/" className="relative inline-block" aria-label="MarketVyne home">
          <Logo variant="full" height={36} />
        </Link>
        <div className="relative">
          <h2 className="font-display font-black text-4xl">Welcome back.</h2>
          <p className="text-white/70 mt-3 max-w-md">
            Trusted by businesses to find SEO, web design and growth experts who actually deliver.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs text-mv-accent">
            <ShieldCheck weight="duotone" /> Buyer protection · Escrow workflow
          </div>
        </div>
        <div className="text-xs text-white/40 relative">© MarketVyne</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="login-form">
          <div>
            <h1 className="font-display font-black text-3xl text-mv-secondary">Log in</h1>
            <p className="text-sm text-gray-500 mt-1">Welcome back to MarketVyne.</p>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="login-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="login-password" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          <Button disabled={busy} type="submit" data-testid="login-submit"
            className="w-full h-12 bg-mv-primary hover:bg-mv-primary-light font-bold">
            {busy ? "Signing in…" : "Log in"}
          </Button>
          <p className="text-sm text-gray-500 text-center">
            Don't have an account? <Link to="/register" className="text-mv-primary font-bold">Sign up</Link>
          </p>
          <div className="text-xs text-gray-400 text-center pt-3 border-t border-gray-100">
            Demo: <code>buyer@marketvyne.com</code> · <code>Buyer@123</code>
          </div>
        </form>
      </div>
    </div>
  );
}
